/**
 * § 375 — what NumberField accepts as typed text, and the number it reads from it.
 *
 * NumberField read a comma as the decimal mark in every locale, so in en-US a pasted "1,000"
 * stored 1 and "1,299.99" (two marks) was refused without a word. The rule now has two parts,
 * tried in order:
 *
 * 1. **A complete grouping in the runtime locale** — first group of 1–3 digits, then groups of
 *    exactly 3, then an optional decimal part — reads as that number: "1,000" is 1000 in en-US,
 *    "1.000" is 1000 in de-DE.
 * 2. **Anything else reads exactly as it always did**: at most one mark, `.` or `,`, and that mark
 *    is the decimal point. So "1,5", "0,5" and "1,0000" are unchanged in every locale.
 *
 * Only inputs that are valid groupings in the user's own locale change meaning, which is the
 * reading every other program on that machine gives them.
 */

export interface NumberSeparators {
  /** The locale's thousands separator; `''` when it groups with nothing. */
  group: string;
  /** The locale's decimal mark. */
  decimal: string;
}

const FALLBACK: NumberSeparators = { group: ',', decimal: '.' };
const cache = new Map<string, NumberSeparators>();

/** The separators `Intl` formats numbers with in `locale` (the runtime default when omitted). */
export const localeSeparators = (locale?: string): NumberSeparators => {
  const key = locale ?? '';
  const hit = cache.get(key);
  if (hit) return hit;
  let separators = FALLBACK;
  try {
    const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
    const decimal = parts.find((part) => part.type === 'decimal')?.value;
    const group = parts.find((part) => part.type === 'group')?.value ?? '';
    if (decimal && group !== decimal) separators = { group, decimal };
  } catch {
    /* An unknown locale tag: read numbers the en way rather than not at all. */
  }
  cache.set(key, separators);
  return separators;
};

/** The legacy draft: digits, at most one mark (`.` or `,`), an optional leading minus. */
const LEGACY = /^-?\d*(?:[.,]\d*)?$/;

const escape = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/* A locale that groups with a (no-break) space accepts any space-like character typed for it.
   `\s` already includes U+00A0 and U+202F, the no-break spaces locales group with. */
const SPACE_LIKE = /^\s$/;
const groupClass = (group: string): string => (SPACE_LIKE.test(group) ? '[\\s\\u00a0\\u202f]' : escape(group));

const groupedPattern = ({ group, decimal }: NumberSeparators): RegExp | null =>
  group === ''
    ? null
    : new RegExp(`^-?\\d{1,3}(?:${groupClass(group)}\\d{3})+(?:${escape(decimal)}\\d*)?$`);

/** Whether `raw` may stand in the field while the user types: a legacy draft, or a complete grouping. */
export const isNumberDraft = (raw: string, separators: NumberSeparators): boolean =>
  LEGACY.test(raw) || (groupedPattern(separators)?.test(raw) ?? false);

/** The number `raw` reads as, or `null` for an empty or unreadable field. */
export const parseNumberDraft = (raw: string, separators: NumberSeparators): number | null => {
  const text = raw.trim();
  const grouped = groupedPattern(separators);
  let normalised: string | null = null;
  if (grouped?.test(text)) {
    normalised = text
      .replace(new RegExp(groupClass(separators.group), 'g'), '')
      .replace(separators.decimal, '.');
  } else if (LEGACY.test(text)) {
    normalised = text.replace(',', '.');
  }
  if (normalised === null || normalised === '' || normalised === '-' || normalised === '.' || normalised === '-.') {
    return null;
  }
  const n = Number(normalised);
  return Number.isFinite(n) ? n : null;
};
